import React, { useState, useEffect } from "react";
import { Typography, Box, TablePagination, LinearProgress, Container, ImageList, ImageListItem } from "@material-ui/core";
import { Club } from "api/clubs";
import { getActivities, ActivityItem } from "api/admin/activities";
import { ClubSelect, HelpscoutFab } from "components";
import { Paginated } from "utils/api";
import i18n from "./i18n";
import ActivityCard from "./ActivityCard";
import styles from "./ActivitiesView.module.scss";

// prop types
interface ActivitiesViewProps {
    club: Club | null,
}

// admin activity log view
const ActivitiesView = ({ club }: ActivitiesViewProps) => {
    // ui state
    const [activities, setActivities] = useState<Paginated<ActivityItem>>({ data: [], totalCount: 0 });
    const [query, setQuery] = useState({ rowsPerPage: 10, page: 0, clubId: club?.id });
    const [isLoading, setLoading] = useState(false);

    // fetch activities
    const fetchActivities = async () => {
        setLoading(true);
        setActivities(await getActivities({
            clubId: query.clubId,
            limit: query.rowsPerPage,
            offset: query.rowsPerPage * query.page,
        }));
        setLoading(false);
    };

    // query changed -> fetch data
    useEffect(() => { fetchActivities(); }, [query]); // eslint-disable-line

    // render
    return (
        <>
            <HelpscoutFab club={club} />

            {/* header */}
            <Box className={styles.header}>
                <Typography variant="h6">
                    {i18n.activities}
                </Typography>

                {/* club select */}
                <ClubSelect
                    clubId={query.clubId}
                    selectProps={{ style: { minWidth: "96px" } }}
                    userRoles={["admin", "owner"]}
                    onChange={(clubId) => setQuery({ ...query, clubId, page: 0 })}
                />
            </Box>

            {/* activities list */}
            <Container maxWidth="sm">
                <ImageList cols={1} rowHeight="auto" gap={8}>
                    {activities.data.map((activity) => (
                        <ImageListItem classes={{ item: styles.tile }} key={activity.id}>
                            <ActivityCard activity={activity} />
                        </ImageListItem>
                    ))}
                </ImageList>

                {/* loading */}
                {isLoading && <LinearProgress />}
            </Container>

            {/* pagination */}
            <TablePagination
                component="div"
                className={styles.pagination}
                count={activities.totalCount}
                page={query.page}
                rowsPerPage={query.rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                onPageChange={(e, page) => setQuery({ ...query, page })}
                onRowsPerPageChange={(e) => setQuery({ ...query, rowsPerPage: parseInt(e.target.value, 10), page: 0 })}
            />
        </>
    );
};

export default ActivitiesView;
